import { yupResolver } from '@hookform/resolvers/yup'
import { type Resolver } from 'react-hook-form'
import * as yup from 'yup'

import { isCustomUrl } from 'components/scenarios/create/status-checks/third-party-presets'
import {
  AuthenticationFormValues,
  AWSAuthenticationType,
  AWSRoleCreationMethod,
  getAuthenticationFormValidationSchema,
  getExternalIntegrationForObservabilityTool,
  isSelectedObservabilityToolAuthenticated,
  OBSERVABILITY_TOOLS,
  ObservabilityTool
} from 'components/status-checks/authentication-form-utils'
import {
  DEFAULT_POLL_INTERVAL,
  MAXIMUM_POLL_INTERVAL,
  MINIMUM_POLL_INTERVAL
} from 'components/status-checks/common/PollingIntervalField'
import { trans } from 'i18n/translate'
import {
  type EndpointConfiguration,
  ObservabilityToolType,
  PredicateType,
  type ResponseBodyEvaluation,
  type ServiceStatusCheck,
  type ServiceStatusCheckFromStatusCheck,
  type StatusCheck,
  StatusCheckCategory,
  type EvaluationConfigurationCreateRequest,
  type StatusCheckCreateRequest,
  type StatusCheckEvaluationPredicate,
  type StatusCheckTeamExternalIntegration,
  type TeamExternalIntegration
} from 'types/api/status-checks/status-check'
import {
  appdynamicsApiHealthRuleViolationsUrlRegex,
  appdynamicsApplicationUrlRegex
} from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  awsAuthUrlRegex,
  cloudwatchAlarmRegex,
  convertToAWSApiUrl,
  getAWSAccountIdFromIamArn
} from 'utils/status-checks/status-check-integrations-util-aws'
import {
  datadogMonitorRegex,
  getDatadogMonitorIdFromUrl
} from 'utils/status-checks/status-check-integrations-util-datadog'
import { getDatadogEUMonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import { getDatadogUS1FEDMonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import { getDatadogUS3MonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import { getDatadogUS5MonitorIdFromUrl } from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import {
  dynatraceApiProblemsRegex,
  dynatraceEntityUrlRegex,
  dynatraceHostsUrlRegex,
  dynatraceMonitorsUrlRegex
} from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  formatResponseBodyEvaluationPredicatesGrafana,
  grafanaApiAlertsRegex,
  grafanaRegex
} from 'utils/status-checks/status-check-integrations-util-grafana'
import { isValidUrl } from 'utils/strings'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'
import {
  computeResponseBodyEvaluation,
  convertToApiUrl,
  defaultStatusCheckEvaluationConfiguration,
  encodeQueryParams,
  formatResponseBodyEvaluationPredicates,
  getDomainNameFromIntegration,
  getIntegrationTypeFromObservabilityToolTypeAndDomain,
  isValidStatusCodeOrRange
} from 'utils/team-external-integrations/team-external-integrations-util'
import { DATADOG_MONITOR_PAGE_URLS } from 'utils/third-party'
import { ms } from 'utils/time/millisecond'
import { allowedIntegrationURLs } from 'utils/yup-additions'

export const MIN_OK_LATENCY_MS = 50
export const MAX_OK_LATENCY_MS = ms.seconds(10)

export interface StatusCheckFormEvaluationConfiguration {
  healthyStatusCode: string
  isCustomRequestTimeout: boolean
  requestTimeout: number | null
  responseBodyEvaluation?: ResponseBodyEvaluation
}

/**
 * Types, Enums, and Interfaces
 */
export interface StatusCheckFormValues extends AuthenticationFormValues {
  category: StatusCheckCategory
  endpointConfiguration?: EndpointConfiguration
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration
  healthyStatusCode: string
  method?: 'GET' | 'POST'
  monitorUrl: string
  pollingIntervalSeconds: string
  statusCheckName?: string
}

export enum StatusCheckMonitorSelectionRadioValue {
  URL = 'url',
  API = 'api',
  SEARCH = 'search'
}

export enum StatusCheckCategoryLabel {
  ERRORS = 'Errors',
  LATENCY = 'Latency',
  REQUESTS = 'Requests',
  UNKNOWN = 'Unknown'
}

/**
 * Constants
 */

// equivalent of 50Kb
export const STATUS_CHECK_PAYLOAD_MAX_CONTENT_LENGTH = 51_200

export const STATUS_CHECK_CATEGORIES = [
  [StatusCheckCategory.ERRORS, StatusCheckCategoryLabel.ERRORS],
  [StatusCheckCategory.LATENCY, StatusCheckCategoryLabel.LATENCY],
  [StatusCheckCategory.REQUESTS, StatusCheckCategoryLabel.REQUESTS],
  [StatusCheckCategory.UNKNOWN, StatusCheckCategoryLabel.UNKNOWN]
] as const

export const STATUS_CHECK_CATEGORIES_LOOKUP = Object.fromEntries(
  STATUS_CHECK_CATEGORIES
)

// This value establishes a consistent width for images inserted into the menu items of MUI's Select component. It is
// necessary to hard define this in order to prevent the Select component's textfield from expanding and causing UI/UX
// elements to become mis-aligned
export const LIST_ITEM_MIN_WIDTH = 36

/**
 * Validation Schemas
 */
function validateMonitorUrl(value: string, integrationType: IntegrationType) {
  try {
    new URL(value)
  } catch (e) {
    return false
  }

  if (integrationType === IntegrationType.Grafana) {
    return grafanaRegex.test(value) || grafanaApiAlertsRegex.test(value)
  }

  if (integrationType === IntegrationType.Dynatrace) {
    return (
      dynatraceApiProblemsRegex.test(value) ||
      dynatraceMonitorsUrlRegex.test(value) ||
      dynatraceHostsUrlRegex.test(value) ||
      dynatraceEntityUrlRegex.test(value)
    )
  }

  if (integrationType === IntegrationType.Appdynamics) {
    return (
      appdynamicsApplicationUrlRegex.test(value) ||
      appdynamicsApiHealthRuleViolationsUrlRegex.test(value)
    )
  }

  return Boolean(
    allowedIntegrationURLs[
      integrationType ||
        IntegrationType.Custom ||
        IntegrationType.LoadGeneratorCustom
    ].find((u) => u.test(value))
  )
}

const statusCheckEvaluationPredicateSchema: yup.SchemaOf<StatusCheckEvaluationPredicate> =
  yup.object({
    type: yup.mixed<PredicateType>(),
    jqQuery: yup.string(),
    jpQuery: yup.string(),
    comparator: yup.string(),
    rValue: yup.mixed()
  })

const responseBodyEvaluationSchema: yup.SchemaOf<ResponseBodyEvaluation> =
  yup.object({
    op: yup.mixed<'AND' | 'OR'>().oneOf(['AND', 'OR']).required(),
    predicates: yup.array().of(statusCheckEvaluationPredicateSchema).required()
  })

const healthyStatusCodeSchema = yup
  .string()
  .test(
    'is-healthy-status-code-valid',
    'Use a single status code (200) or a range (200-299)',
    (value) => isValidStatusCodeOrRange(value)
  )
  .required('Please enter a value for a healthy status code')

const requestTimeoutSchema = yup.number().when('isCustomRequestTimeout', {
  is: true,
  then: (schema) =>
    schema
      .required('Request timeout is required')
      .min(
        MIN_OK_LATENCY_MS,
        `Request timeout must be between ${MIN_OK_LATENCY_MS} and ${MAX_OK_LATENCY_MS}`
      )
      .max(
        MAX_OK_LATENCY_MS,
        `Request timeout must be between ${MIN_OK_LATENCY_MS} and ${MAX_OK_LATENCY_MS}`
      )
      .typeError('Request timeout must be a number'),
  otherwise: (schema) => schema.nullable().notRequired()
})

export function getStatusCheckFormValidationSchema(
  externalIntegrations: TeamExternalIntegration[],
  existingStatusChecks: ServiceStatusCheckFromStatusCheck[],
  shouldIncludeMethodField: boolean
) {
  return yup.object().shape({
    category: yup.string().oneOf(Object.values(StatusCheckCategory)),
    pollingIntervalSeconds: yup
      .number()
      .default(DEFAULT_POLL_INTERVAL)
      .min(
        MINIMUM_POLL_INTERVAL,
        `Polling interval must not be shorter than ${MINIMUM_POLL_INTERVAL} seconds`
      )
      .max(
        MAXIMUM_POLL_INTERVAL,
        `Polling interval must not be longer than ${MAXIMUM_POLL_INTERVAL} seconds`
      )
      .typeError('A polling interval is required'),
    ...(shouldIncludeMethodField && {
      endpointConfiguration: yup.object().shape({
        method: yup
          .string()
          .test(
            'is-valid-method',
            'Value must be a valid request method',
            (value) => value === 'GET' || value === 'POST'
          )
          .required(),
        headers: yup.object().when('method', {
          is: (method: 'POST' | 'GET') => method === 'POST',
          then: (schema) =>
            schema.shape({
              ['content-type']: yup
                .string()
                .max(50, 'Content Types must be 50 characters or less')
                .required(
                  'A ContentType header must be specified when using POST method'
                )
            })
        }),
        payload: yup.string().when('method', {
          is: (method: 'POST' | 'GET') => method === 'POST',
          then: (schema) =>
            schema.required(
              'A Request Body must be specified when using POST method'
            )
        })
      })
    }),
    evaluationConfiguration: yup.object({
      healthyStatusCode: healthyStatusCodeSchema,
      isCustomRequestTimeout: yup.boolean().required(),
      requestTimeout: requestTimeoutSchema,
      responseBodyEvaluation: responseBodyEvaluationSchema
    }),
    healthyStatusCode: healthyStatusCodeSchema,
    monitorUrl: yup
      .string()
      .trim()
      .test(
        'is-valid-url',
        trans('forms.validation.enter_valid_url'),
        (value) => isValidUrl(value)
      )
      .when(
        ['endpointConfiguration', 'observabilityTool'],
        // TS complains about the function arity, but the arguments presented are valid.
        /* @ts-expect-error: type mismatch */
        (endpointConfiguration, observabilityTool, schema) => {
          const { integrationType } = observabilityTool ?? {}
          const existingUrls = existingStatusChecks
            // Filter existingStatusChecks to match currently selected observability tool type, name and method
            .filter((existingStatusCheck) => {
              if (
                // both 'GET' and 'POST' methods can have the same monitor URL
                existingStatusCheck.endpointConfiguration?.method !==
                endpointConfiguration?.method
              )
                return false

              return (
                // Special case for AppD healthrule violations, since they have valid cases to reuse the same URL
                !existingStatusCheck.integration.url?.includes('appdynamics') ||
                !existingStatusCheck.integration.url?.includes(
                  'healthrule-violations'
                ) ||
                (getIntegrationTypeFromObservabilityToolTypeAndDomain(
                  existingStatusCheck.integration.observabilityToolType,
                  existingStatusCheck.integration.domain
                ) === observabilityTool.integrationType &&
                  existingStatusCheck.integration.name ===
                    observabilityTool.name)
              )
            })
            // Map all resulting statusCheck monitor URLs
            .map((filteredExistingStatusCheck) => {
              // Special case for Datadog US1 monitors, since we accept two different URL formats
              if (
                observabilityTool.type === ObservabilityToolType.DATADOG &&
                observabilityTool.integrationType === IntegrationType.Datadog
              ) {
                return convertDatadogAppUrlToApiUrl(
                  filteredExistingStatusCheck.integration.url
                )
              }

              return filteredExistingStatusCheck.integration.url
            })

          if (observabilityTool?.type !== ObservabilityToolType.AWS) {
            return schema
              .test(
                'is-monitor-url-valid',
                trans('forms.validation.enter_valid_url'),
                (value) => validateMonitorUrl(value, integrationType)
              )
              .test(
                'is-datadog-monitor-url-already-used',
                trans('forms.validation.error.monitor_url_already_exists'),
                (value) => {
                  // Special test case for Datadog US1 monitors, since we accept two different URL formats
                  if (
                    observabilityTool.type === ObservabilityToolType.DATADOG &&
                    observabilityTool.integrationType ===
                      IntegrationType.Datadog
                  ) {
                    return !existingUrls.includes(
                      convertDatadogAppUrlToApiUrl(value)
                    )
                  }

                  return true
                }
              )
              .required(trans('forms.validation.enter_monitor_url'))
              .notOneOf(
                existingUrls,
                trans('forms.validation.error.monitor_url_already_exists')
              )
          } else {
            return schema
              .test(
                'is-aws-monitor-url-valid',
                trans('forms.validation.error.invalid_url'),
                (value) => {
                  return (
                    awsAuthUrlRegex.test(value) ||
                    cloudwatchAlarmRegex.test(value)
                  )
                }
              )
              .test(
                'is-aws-monitor-url-already-used',
                trans('forms.validation.error.monitor_url_already_exists'),
                (value) => {
                  // Special test case for aws monitors, since we accept two different URL formats
                  return !existingUrls.includes(
                    encodeQueryParams(convertToAWSApiUrl(value))
                  )
                }
              )
              .required(trans('forms.validation.enter_monitor_url'))
              .notOneOf(
                existingUrls,
                trans('forms.validation.error.monitor_url_already_exists')
              )
          }
        }
      ),
    statusCheckName: yup.string().when('observabilityTool', {
      is: (observabilityTool) =>
        observabilityTool &&
        observabilityTool?.type !== ObservabilityToolType.DATADOG &&
        isSelectedObservabilityToolAuthenticated(
          observabilityTool,
          externalIntegrations
        ),
      then: (schema) =>
        schema
          .min(1, trans('forms.validation.error.empty_status_check_name'))
          .required(trans('forms.validation.enter_status_check_name'))
    })
  })
}

/**
 * Utility functions
 */

export function buildStatusCheckFormValidationSchema({
  existingStatusChecks,
  externalIntegrations,
  statusCheck
}: {
  existingStatusChecks: ServiceStatusCheckFromStatusCheck[]
  externalIntegrations: TeamExternalIntegration[]
  statusCheck?: ServiceStatusCheckFromStatusCheck
}): Resolver<StatusCheckFormValues, number | undefined> {
  return function (values, stepNumber, options) {
    const shouldIncludeMethodField =
      values.observabilityTool?.type === ObservabilityToolType.CUSTOM

    // As part of the creation flow, we want to take into consideration the active form step,
    // and conditionally build the appropriate schema.
    const schema =
      stepNumber === 0
        ? getAuthenticationFormValidationSchema(externalIntegrations)
        : getStatusCheckFormValidationSchema(
            externalIntegrations,
            existingStatusChecks.filter(({ id }) => id !== statusCheck?.id),
            shouldIncludeMethodField
          )

    return yupResolver(schema)(values, stepNumber, options)
  }
}

/**
 * Converts any valid Datadog US1 monitor URL to a standard format for validation purposes only.
 */
function convertDatadogAppUrlToApiUrl(url: string): string {
  if (datadogMonitorRegex.test(url)) {
    const isAPPSubDomain = datadogMonitorRegex.exec(url)?.[1] === 'app'
    const isAPIUrl = datadogMonitorRegex.exec(url)?.[2] === 'api/v1/monitor'
    const monitorId = datadogMonitorRegex.exec(url)?.[3]

    // Convert any "https://app..." URLs to "https://api..."
    if (isAPPSubDomain && monitorId) {
      return `https://api.datadoghq.com/api/v1/monitor/${monitorId}`
    }

    if (isAPIUrl) {
      return url
    }

    if (monitorId) {
      return `https://api.datadoghq.com/api/v1/monitor/${monitorId}`
    }
  }

  return null
}

/**
 * Matches an existing status check to the currently selected observability tool and monitor URL.
 */
export function getExistingStatusCheckByMonitorUrl(
  existingStatusChecks: ServiceStatusCheck[],
  observabilityTool: ObservabilityTool,
  monitorUrl: string
) {
  const isDatadogObservabilityTool =
    observabilityTool.type === ObservabilityToolType.DATADOG

  return (
    existingStatusChecks
      // Filter existingStatusChecks to match currently selected observability tool type and name
      .filter((existingStatusCheck) => {
        return (
          getIntegrationTypeFromObservabilityToolTypeAndDomain(
            existingStatusCheck.integration.observabilityToolType,
            existingStatusCheck.integration.domain
          ) === observabilityTool.integrationType &&
          existingStatusCheck.integration.name === observabilityTool.name
        )
      })
      .find((filteredExistingStatusCheck) => {
        if (isDatadogObservabilityTool) {
          // In order for this check to work with Datadog integrations, we have to convert both the
          // existing status check URL and the monitor URL to Datadog's API URL format
          const existingDatadogStatusCheckApiUrl = convertDatadogAppUrlToApiUrl(
            filteredExistingStatusCheck.integration.url
          )
          const datadogMonitorApiUrl = convertDatadogAppUrlToApiUrl(monitorUrl)

          return existingDatadogStatusCheckApiUrl === datadogMonitorApiUrl
        }

        return filteredExistingStatusCheck.integration.url === monitorUrl
      })
  )
}

/**
 * Builds a TeamExternalIntegration object for a Status Check from the current form values
 */
export function buildTeamExternalIntegration({
  externalIntegration,
  headers,
  isPrivateNetworkIntegration,
  observabilityTool,
  observabilityToolNickname,
  apiBaseUrl,
  authenticationUrl,
  monitorUrl,
  domain,
  awsAccessKeyID,
  awsSecretAccessKey,
  awsIamRoleArn,
  awsAuthenticationType
}: StatusCheckFormValues | AuthenticationFormValues): TeamExternalIntegration {
  const newExternalIntegration = {
    ...externalIntegration,
    headers,
    isPrivateNetworkIntegration,
    observabilityToolType: observabilityTool.type
  }

  // A special case for Datadog's "US1" domain
  if (observabilityTool.integrationType === IntegrationType.Datadog) {
    newExternalIntegration.domain = null
  } else {
    newExternalIntegration.domain = domain?.name || null
  }

  // monitorUrl should override all others
  if (observabilityTool.integrationType === IntegrationType.Custom) {
    newExternalIntegration.name = observabilityToolNickname
    newExternalIntegration.url = monitorUrl || authenticationUrl
  } else {
    newExternalIntegration.url = monitorUrl || apiBaseUrl
  }

  if (observabilityTool.integrationType === IntegrationType.AWS) {
    if (
      awsIamRoleArn &&
      awsAuthenticationType === AWSAuthenticationType.IAMRole
    ) {
      const domainName = getAWSAccountIdFromIamArn(awsIamRoleArn)

      newExternalIntegration.domain = domainName
      newExternalIntegration.integrationSpecificValues = {
        AWS_IAM_ROLE_ARN: awsIamRoleArn
      }
    } else {
      newExternalIntegration.domain = domain?.name || null
      newExternalIntegration.integrationSpecificValues = {
        AWS_ACCESS_KEY_ID: awsAccessKeyID,
        AWS_SECRET_ACCESS_KEY: awsSecretAccessKey
      }
    }
    newExternalIntegration.url = monitorUrl || authenticationUrl
    newExternalIntegration.name = ObservabilityToolType.AWS
  }

  return newExternalIntegration
}

function validatePredicate(predicate: StatusCheckEvaluationPredicate) {
  return (
    predicate.type !== null &&
    predicate.comparator !== '' &&
    predicate.jpQuery !== '' &&
    predicate.rValue !== ''
  )
}

/**
 * Builds a StatusCheckEvaluationConfiguration object from passed in form values.
 */
export function buildEvaluationConfigurationFromFormValues(
  formValues: StatusCheckFormValues
): StatusCheckFormEvaluationConfiguration {
  const { evaluationConfiguration, healthyStatusCode } = formValues

  const { responseBodyEvaluation, ...evaluationConfigurationRest } =
    evaluationConfiguration || {}

  const predicates = evaluationConfiguration?.responseBodyEvaluation?.predicates

  const arePredicatesPopulated = predicates?.reduce(
    (isValid, predicate) => (validatePredicate(predicate) ? isValid : false),
    true
  )

  return {
    ...evaluationConfigurationRest,
    healthyStatusCode,
    responseBodyEvaluation: {
      ...responseBodyEvaluation,
      ...(arePredicatesPopulated && { predicates })
    }
  }
}

/**
 * Builds the teamExternalIntegration object for the API call to create a new Status Check
 */
export function buildStatusCheckTeamExternalIntegration(
  formValues: StatusCheckFormValues
): StatusCheckTeamExternalIntegration {
  const integration = buildTeamExternalIntegration(formValues)
  const isCustomIntegration = integration.type === IntegrationType.Custom
  const domainName = formValues.domain.name

  const statusCheckTeamExternalIntegration: StatusCheckTeamExternalIntegration =
    {
      name: formValues.externalIntegration.name,
      observabilityToolType: formValues.observabilityTool.type,
      domain: domainName === 'US1' ? null : domainName // Special case for Datadog's US1 domain
    }

  /**
   * custom integration needs to be configured on the team external integration level first before
   * we can add this object to the request body
   */
  if (
    (isCustomIntegration && integration.isConfigured) ||
    (!isCustomIntegration && !integration.isConfigured)
  ) {
    statusCheckTeamExternalIntegration.name = integration.name
  }

  return statusCheckTeamExternalIntegration
}

export async function buildSaveStatusCheckRequest(
  formValues: StatusCheckFormValues,
  currentTeamId: string
): Promise<StatusCheckCreateRequest> {
  const isCustomObservabilityTool =
    formValues.observabilityTool?.type === ObservabilityToolType.CUSTOM
  const integration = buildTeamExternalIntegration(formValues)
  const teamExternalIntegration =
    buildStatusCheckTeamExternalIntegration(formValues)
  const newEvaluationConfiguration =
    buildEvaluationConfigurationFromFormValues(formValues)
  const evaluationConfiguration: EvaluationConfigurationCreateRequest = {
    ...(formValues.evaluationConfiguration.isCustomRequestTimeout &&
      newEvaluationConfiguration.requestTimeout !== null && {
        okLatencyMaxMs: newEvaluationConfiguration.requestTimeout
      }),
    okStatusCodes: [newEvaluationConfiguration.healthyStatusCode]
  }

  if (
    newEvaluationConfiguration.responseBodyEvaluation &&
    integration.type === IntegrationType.Custom
  ) {
    evaluationConfiguration.responseBodyEvaluation =
      formatResponseBodyEvaluationPredicates(
        newEvaluationConfiguration.responseBodyEvaluation
      )
  } else if (integration.type === IntegrationType.Grafana) {
    evaluationConfiguration.responseBodyEvaluation =
      formatResponseBodyEvaluationPredicatesGrafana(
        await convertToApiUrl(integration, currentTeamId)
      )
  } else {
    evaluationConfiguration.responseBodyEvaluation =
      computeResponseBodyEvaluation(integration)
  }

  const { method, headers, payload } = formValues.endpointConfiguration ?? {}
  const endpointConfiguration = !isCustomObservabilityTool
    ? undefined
    : method === 'POST'
    ? { method, headers, payload }
    : { method }

  return {
    category: formValues.category,
    endpointConfiguration: {
      url: encodeQueryParams(await convertToApiUrl(integration, currentTeamId)),
      ...endpointConfiguration
    },
    evaluationConfiguration,
    isContinuous: true,
    isPrivateNetwork: formValues.isPrivateNetworkIntegration,
    name: formValues.statusCheckName ?? '',
    pollingIntervalSeconds: parseInt(formValues.pollingIntervalSeconds, 10),
    rawEndpointConfiguration: { url: integration.url },
    teamExternalIntegration,
    thirdPartyPresets: integration.type
  }
}

export function getDomainNameFromTeamExternalIntegration(
  integration: TeamExternalIntegration | StatusCheckTeamExternalIntegration
) {
  if (
    integration?.observabilityToolType === ObservabilityToolType.DATADOG &&
    !integration?.domain
  ) {
    return '/US1'
  }

  return integration?.domain ? `/${integration.domain}` : ''
}

function getObservabilityToolFromExistingStatusCheck(
  statusCheck: ServiceStatusCheck,
  domainName: string
) {
  const {
    domain,
    name: observabilityToolName = '',
    type: integrationType,
    observabilityToolType
  } = statusCheck.integration

  const observabilityTool: ObservabilityTool = {
    name: observabilityToolName
  }

  if (observabilityToolType) {
    observabilityTool.type = observabilityToolType
  }

  if (integrationType) {
    observabilityTool.integrationType = integrationType
  }

  if (observabilityToolType === ObservabilityToolType.AWS) {
    observabilityTool.selectedDomain = domain
  }

  // Configures any new custom domains to the observability tool so they are immediately available
  if (observabilityTool.type === ObservabilityToolType.CUSTOM && domainName) {
    observabilityTool.domains = [
      {
        name: domain,
        integrationType: IntegrationType.Custom
      }
    ]
  } else {
    observabilityTool.domains = OBSERVABILITY_TOOLS.find(
      (staticObservabilityTool) =>
        staticObservabilityTool.type === observabilityTool.type
    )?.domains
  }

  return observabilityTool
}

/**
 * Returns default form values for the Create Status Check form
 */
export function getDefaultStatusCheckFormValues(): StatusCheckFormValues {
  return {
    apiBaseUrl: '',
    authenticationUrl: '',
    awsAccessKeyID: '',
    awsSecretAccessKey: '',
    awsAuthenticationType: AWSAuthenticationType.IAMRole,
    awsRoleCreationMethod: AWSRoleCreationMethod.CloudFormation,
    awsIamRoleArn: '',
    category: StatusCheckCategory.UNKNOWN,
    customDomainName: '',
    domain: { name: '' },
    endpointConfiguration: { method: 'GET' },
    evaluationConfiguration: defaultStatusCheckEvaluationConfiguration,
    externalIntegration: undefined,
    headers: [],
    healthyStatusCode:
      defaultStatusCheckEvaluationConfiguration.healthyStatusCode,
    isPrivateNetworkIntegration: false,
    monitorUrl: '',
    multipleRegions: false,
    observabilityTool: { name: '' },
    observabilityToolNickname: '',
    pollingIntervalSeconds: '',
    statusCheckName: ''
  }
}

/**
 * Build default EditStatusCheck form values for editing a shared status check
 */
export function getDefaultStatusCheckFormValuesFromStatusCheck(
  statusCheck: ServiceStatusCheckFromStatusCheck,
  externalIntegrations: TeamExternalIntegration[]
): StatusCheckFormValues {
  const { category, name: statusCheckName = '' } = statusCheck
  const {
    domain: observabilityToolDomain,
    isPrivateNetworkIntegration,
    name: observabilityToolName = '',
    type: integrationType,
    observabilityToolType,
    url: monitorUrl = ''
  } = statusCheck.integration

  const isCustomObservabilityTool =
    observabilityToolType === ObservabilityToolType.CUSTOM
  const isAWSObservabilityTool =
    observabilityToolType === ObservabilityToolType.AWS

  const domainName = getDomainNameFromIntegration(statusCheck.integration) ?? ''

  const observabilityTool: ObservabilityTool =
    getObservabilityToolFromExistingStatusCheck(statusCheck, domainName)

  const observabilityToolNickname = isCustomObservabilityTool
    ? observabilityToolName
    : ''

  const domain = domainName
    ? { name: domainName, integrationType }
    : { name: '' }

  const customDomainName =
    isCustomObservabilityTool && observabilityTool.domains?.length
      ? observabilityToolDomain ?? undefined
      : ''

  const apiBaseUrl = !isCustomObservabilityTool ? monitorUrl : ''

  const authenticationUrl =
    isCustomObservabilityTool || isAWSObservabilityTool ? monitorUrl : ''

  const externalIntegration = getExternalIntegrationForObservabilityTool(
    observabilityTool,
    externalIntegrations
  )

  const { headers = [] } = externalIntegration

  const evaluationConfiguration =
    statusCheck.evaluationConfiguration ||
    defaultStatusCheckEvaluationConfiguration

  const healthyStatusCode =
    statusCheck?.evaluationConfiguration?.healthyStatusCode ||
    defaultStatusCheckEvaluationConfiguration.healthyStatusCode

  const pollingIntervalSeconds = `${statusCheck.pollingIntervalSeconds ?? ''}`

  const configPredicates =
    statusCheck?.evaluationConfiguration?.responseBodyEvaluation?.predicates

  // Defaulting to an empty array here will insure that we do not render an empty
  // 'predicates' fieldset when modifying a HC with a GET method configuration.
  const predicates = configPredicates?.length ? configPredicates : []

  const requestTimeout =
    statusCheck?.evaluationConfiguration?.requestTimeout ??
    defaultStatusCheckEvaluationConfiguration.requestTimeout

  return {
    apiBaseUrl,
    authenticationUrl,
    category,
    customDomainName,
    domain,
    evaluationConfiguration: {
      ...evaluationConfiguration,
      responseBodyEvaluation: {
        op: evaluationConfiguration.responseBodyEvaluation?.op || 'AND',
        predicates
      },
      isCustomRequestTimeout: Boolean(requestTimeout)
    },
    externalIntegration,
    headers,
    healthyStatusCode,
    isPrivateNetworkIntegration: Boolean(isPrivateNetworkIntegration),
    monitorUrl,
    multipleRegions: Boolean(customDomainName),
    observabilityTool,
    observabilityToolNickname,
    statusCheckName,
    pollingIntervalSeconds,
    requestTimeout,
    ...(isAWSObservabilityTool && {
      awsAccessKeyID:
        externalIntegration.integrationSpecificValues?.AWS_ACCESS_KEY_ID ?? '',
      awsSecretAccessKey:
        externalIntegration.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ??
        '',
      awsIamRoleArn:
        externalIntegration.integrationSpecificValues?.AWS_IAM_ROLE_ARN ?? ''
    }),
    ...(isCustomObservabilityTool && {
      endpointConfiguration: {
        headers: {
          'content-type':
            statusCheck.endpointConfiguration?.headers?.['content-type'] ?? ''
        },
        method: statusCheck.endpointConfiguration?.method ?? 'GET',
        payload: statusCheck.endpointConfiguration?.payload ?? ''
      }
    })
  }
}

/**
 * Returns the URL to the monitor page for the given status check. Currently only Datadog is supported.
 */
export function getMonitorPageUrl(
  url: string,
  integrationType: IntegrationType
): string {
  if (integrationType === IntegrationType.Datadog) {
    const monitorId = getDatadogMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogEU) {
    const monitorId = getDatadogEUMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_eu}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS3) {
    const monitorId = getDatadogUS3MonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us3}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS5) {
    const monitorId = getDatadogUS5MonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us5}/${monitorId}`
  }
  if (integrationType === IntegrationType.DatadogUS1Fed) {
    const monitorId = getDatadogUS1FEDMonitorIdFromUrl(url)
    return `${DATADOG_MONITOR_PAGE_URLS.datadog_us1_fed}/${monitorId}`
  }

  return url
}

/**
 * Adds a space after the first colon in a status check tag
 */
export function formatStatusCheckTag(tag: string) {
  return tag.replace(/:/, ': ')
}

/**
 * Returns true if the passed in status check is a custom status check
 */
export function isCustomStatusCheck(statusCheck?: StatusCheck) {
  // Default to true if the status check is not defined
  if (!statusCheck) {
    return true
  }

  return isCustomUrl(
    statusCheck.endpointConfiguration?.url,
    statusCheck.teamExternalIntegration?.observabilityToolType
  )
}
